// @ts-strict-ignore
import { Inject, Injectable, Type } from '@angular/core';

import { WINDOW } from 'src/app/utils/window.token';

import { LoggerService } from '../logger/logger.service';

import { LazyComponentId, LazyComponentRegistryItem, lazyComponentsRegistry } from './lazy-components-registry';

/**
 * This services uses angular's internal lazy-loading strategy to load standalone components
 * on demand (on custom triggers, independent from routing).
 *
 * Modules need to...
 *  - have an entry in `LazyModuleId` enum
 *  - be registered in `lazyModulesRegistry` map
 *  - have a static class property `rootComponent` giving the component name
 *    used for bootstrapping the module
 *
 * LazyComponentsService does only load standalone components and not take care of any factory
 * creation or injection. See `LazyHostComponent` for a full usage example.
 */
@Injectable({
  providedIn: 'root'
})
export class LazyComponentsService {
  public constructor(
    private readonly loggerService: LoggerService,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  public async loadComponent<T = unknown>(id: LazyComponentId): Promise<Type<T>> {
    const validComponentId = this.ensureValidComponentId(id);
    const componentData = this.getComponentDataFromRegistry(validComponentId);
    const { requiresVue, path } = componentData;

    // TODO: extra vue support should be dropped once owcc runs "integrated-only"
    if (requiresVue) {
      await this.loadVue();
    }

    const component = await path();

    try {
      return component[validComponentId] as Type<T>;
    } catch (error) {
      this.loggerService.error(`Error compiling lazy component "${validComponentId}": `, error);
      throw error;
    }
  }

  private async loadVue(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.window.top.Vue) {
        resolve();
        return;
      }

      import('vue')
        .then(module => {
          this.window.top.Vue = module.default;
          resolve();
        })
        .catch((error: unknown) => {
          reject(error as Error);
        });
    });
  }

  /**
   * Retrieve component data from registry
   */
  private getComponentDataFromRegistry(id: LazyComponentId): LazyComponentRegistryItem {
    try {
      const data = lazyComponentsRegistry.get(id);
      return data;
    } catch (error) {
      this.loggerService.error(`Unknown lazy component "${id}"`, error);
      throw error;
    }
  }

  /**
   * Ensure given component name has the "Component" suffix
   */
  private ensureValidComponentId(id: string): LazyComponentId {
    return (/[\dA-Za-z]+Component/.test(id) ? id : `${id}Component`) as LazyComponentId;
  }
}
